import { inject, Injectable, Injector, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { environment } from '../../../environments/environment';
import {
  AUTH_STORAGE_KEY,
  getAuthCookie,
} from '../../utils/auth-session.util';
import {
  getAccessTokenExpiresAtMs,
  getAccessTokenJti,
} from '../../utils/session-auth-error.util';
import { TokenRefreshService } from '../token-refresh.service';
import { AuthService } from '../auth.service';
import {
  SessionExpiryDialogComponent,
  SessionExpiryDialogResult,
} from './session-expiry-dialog.component';
import { authFlowDebug } from '../../utils/auth-flow-debug.util';

const MAX_SCHEDULE_DELAY_MS = 2_147_483_647;

@Injectable({
  providedIn: 'root',
})
export class SessionExpiryWarningService {
  private readonly dialog = inject(MatDialog);
  private readonly ngZone = inject(NgZone);
  private readonly injector = inject(Injector);

  private readonly warningBeforeMs =
    environment.sessionExpiryWarningMs ?? 300_000;

  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private dialogRef: MatDialogRef<
    SessionExpiryDialogComponent,
    SessionExpiryDialogResult
  > | null = null;
  private modalOpen = false;
  private suppressDialogResult = false;
  private autoLogoutTriggered = false;
  private modalShownForTokenJti: string | null = null;

  startMonitoring(): void {
    this.stopMonitoring();
    const token = this.getAccessToken();
    if (!token) {
      authFlowDebug.log(
        'session_expiry_monitoring_started',
        'SessionExpiryWarningService.startMonitoring',
        { detail: 'Skipped — no access token found.' },
      );
      return;
    }
    authFlowDebug.log(
      'session_expiry_monitoring_started',
      'SessionExpiryWarningService.startMonitoring',
      {
        data: {
          warningBeforeMs: this.warningBeforeMs,
          tokenJti: getAccessTokenJti(token),
          expiresAt: new Date(getAccessTokenExpiresAtMs(token) ?? 0).toISOString(),
        },
      },
    );
    this.scheduleWarningForToken(token);
  }

  reschedule(): void {
    this.closeModal();
    this.modalShownForTokenJti = null;
    this.startMonitoring();
  }

  stopMonitoring(): void {
    this.clearWarningTimer();
    this.closeModal();
    this.modalShownForTokenJti = null;
    this.autoLogoutTriggered = false;
  }

  private getAccessToken(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return (
      localStorage.getItem(AUTH_STORAGE_KEY) ??
      localStorage.getItem('accessToken') ??
      getAuthCookie(AUTH_STORAGE_KEY)
    );
  }

  private scheduleWarningForToken(token: string): void {
    const expiresAtMs = getAccessTokenExpiresAtMs(token);
    if (expiresAtMs === null) {
      return;
    }

    const warningAtMs = expiresAtMs - this.warningBeforeMs;
    const delayMs = warningAtMs - Date.now();
    const tokenJti = getAccessTokenJti(token) ?? token.slice(-16);

    if (delayMs <= 0) {
      if (this.modalShownForTokenJti === tokenJti) {
        return;
      }
      this.ngZone.run(() => this.openExpiryModal(tokenJti, expiresAtMs));
      return;
    }

    const safeDelay = Math.min(delayMs, MAX_SCHEDULE_DELAY_MS);
    authFlowDebug.log(
      'session_expiry_warning_scheduled',
      'SessionExpiryWarningService.scheduleWarningForToken',
      {
        data: {
          tokenJti,
          delayMs: safeDelay,
          warningAt: new Date(warningAtMs).toISOString(),
          expiresAt: new Date(expiresAtMs).toISOString(),
        },
      },
    );
    this.warningTimer = setTimeout(() => {
      const currentToken = this.getAccessToken();
      const currentJti =
        getAccessTokenJti(currentToken) ?? currentToken?.slice(-16);
      if (!currentToken || currentJti !== tokenJti) {
        return;
      }
      const currentExpiry = getAccessTokenExpiresAtMs(currentToken);
      if (currentExpiry === null) {
        return;
      }
      this.ngZone.run(() => this.openExpiryModal(tokenJti, currentExpiry));
    }, safeDelay);
  }

  private openExpiryModal(tokenJti: string, expiryAtMs: number): void {
    if (this.modalOpen || this.modalShownForTokenJti === tokenJti) {
      return;
    }

    if (!this.getAccessToken()) {
      return;
    }

    this.modalOpen = true;
    this.modalShownForTokenJti = tokenJti;
    this.autoLogoutTriggered = false;
    this.suppressDialogResult = false;

    authFlowDebug.log(
      'session_expiry_modal_opened',
      'SessionExpiryWarningService.openExpiryModal',
      {
        data: {
          tokenJti,
          expiryAt: new Date(expiryAtMs).toISOString(),
          remainingMs: expiryAtMs - Date.now(),
        },
        level: 'warn',
      },
    );

    this.dialogRef = this.dialog.open(SessionExpiryDialogComponent, {
      data: { expiryAtMs },
      width: '420px',
      maxWidth: '94vw',
      panelClass: 'bh-confirm-panel',
      disableClose: true,
      autoFocus: 'button',
      restoreFocus: true,
    });

    this.dialogRef.afterClosed().subscribe(result => {
      this.modalOpen = false;
      this.dialogRef = null;

      if (this.suppressDialogResult) {
        this.suppressDialogResult = false;
        return;
      }

      if (result === 'stay') {
        authFlowDebug.log(
          'session_expiry_stay_clicked',
          'SessionExpiryWarningService',
        );
        this.handleStayLoggedIn();
      } else if (result === 'logout') {
        authFlowDebug.log(
          'session_expiry_logout_clicked',
          'SessionExpiryWarningService',
        );
        this.handleLogoutClicked();
      } else if (result === 'expired') {
        authFlowDebug.log(
          'session_expiry_auto_logout',
          'SessionExpiryWarningService',
          { reason: 'TOKEN_EXPIRED', level: 'warn' },
        );
        this.handleAutoLogout();
      }
    });
  }

  private clearWarningTimer(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  private closeModal(): void {
    if (this.dialogRef) {
      this.suppressDialogResult = true;
      this.dialogRef.close();
      this.dialogRef = null;
    }
    this.modalOpen = false;
  }

  private handleStayLoggedIn(): void {
    this.injector.get(TokenRefreshService).refreshTokens().subscribe({
      next: () => {
        this.modalShownForTokenJti = null;
        this.reschedule();
      },
      error: () => {
        // TokenRefreshService handles logout on refresh failure.
      },
    });
  }

  private handleLogoutClicked(): void {
    this.stopMonitoring();
    this.injector.get(AuthService).logout({
      source: 'SessionExpiryWarningService.handleLogoutClicked',
      reason: 'user_clicked_logout_on_expiry_modal',
    });
  }

  private handleAutoLogout(): void {
    if (this.autoLogoutTriggered) {
      return;
    }
    this.autoLogoutTriggered = true;

    this.stopMonitoring();
    const authService = this.injector.get(AuthService);
    authService.notifySessionLogout('TOKEN_EXPIRED');
    authService.logout({
      source: 'SessionExpiryWarningService.handleAutoLogout',
      reason: 'jwt_expiry_countdown_finished',
    });
  }
}
